import React from 'react'
import styled from '@emotion/styled'
import { font, colors, spacing, radius, measurements } from '@mobi/component-library/Theme/Common'
import { TabButton, Tabs } from '@mobi/component-library/Navigation/Tabs'
import { Button, PillButton } from '@mobi/component-library/Common/V2/Buttons'
import { Icon } from '@mobi/component-library/Common/V2/Icon'
import { ContextMenu } from '@mobi/component-library/Overlays/ContextMenu'
import { useBodyScrollLock } from '@mobi/utils/hooks/useBodyScrollLock'
import { useSearchParamState } from '@core/Utils/hooks/useSearchParamState'
import { useClickOutside } from '@core/Utils/hooks/useClickOutside'
import { useScrollRestoreOverride } from '@core/Areas/AppHeader/Components/ScrollRestoration/hooks/useScrollRestoreOverride'
import type { FollowMeProfile } from '@core/Areas/FollowMe/types'
import { ProfileCard } from '@core/Areas/FollowMe/Components/Common/ProfileCard'
import { QuickShareButton } from '@core/Areas/FollowMe/Components/QuickShareButton'
import { FeedContainerStyled } from '@core/Areas/FollowMe/Components/Common/FollowMe.styles'
import {
  ReportProfileButton,
  ReportProfileModal,
} from '@core/Areas/FollowMe/Components/Common/ReportProfile'
import { trackBetLoopProfileTabAndPillChange } from '@core/Areas/FollowMe/helpers/analytics'
import {
  IncomingBetOpenPostIdSearchParam,
  IncomingBetViewPostIdSearchParam,
} from '@core/Areas/FollowMe/Components/ProfileDisplay/constants'
import {
  // ProfileBio, // TEMPORARY: AML Compliance - commented out
  FollowStats,
  EmptyProfile,
  PrimaryActionSection,
  ProfileStats,
  ProfileBets,
} from './Components'
import { useShareProfile } from './hooks/useShareProfile'
import { ProfileContext } from './context'
import type { BetOptions, TabContentOptions } from './types'

const enum LocalConstants {
  CardHalfOpenDisplayPoint = 264,
}

export const ProfileDisplay: React.FC<{
  isMyProfile: boolean
  profile: FollowMeProfile
}> = ({ isMyProfile, profile }) => {
  const containerElRef = useBodyScrollLock()
  const profileCardElRef = React.useRef<HTMLDivElement>(null)
  const profileCardContainerElRef = React.useRef<HTMLDivElement>(null)
  const trayHeadElRef = React.useRef<HTMLDivElement>(null)
  const trayProfileInfoElRef = React.useRef<HTMLDivElement>(null)
  const trayShareButtonElRef = React.useRef<HTMLDivElement>(null)

  const [isTrayPinned, setIsTrayPinned] = React.useState(false)
  const [hasCopiedBets, setHasCopiedBets] = React.useState(false)
  const [isReportProfileModalOpen, setIsReportProfileModalOpen] = React.useState(false)
  const [isShareContextMenuOpen, setIsShareContextMenuOpen] = React.useState(false)
  const [pendingBetsCount, setPendingBetsCount] = React.useState<number | null>(null)

  const [currentContentDisplay, setCurrentContentDisplay] = useSearchParamState<TabContentOptions>({
    key: 'tab',
    values: ['pending-bets', 'resulted-bets', 'my-copies', 'stats'],
    defaultValue: 'pending-bets',
  })

  const betOptions: Exclude<TabContentOptions, 'stats'>[] = isMyProfile
    ? ['pending-bets', 'resulted-bets', 'my-copies']
    : ['pending-bets', 'resulted-bets']

  const { canShare, handleShareProfileClick, isLoadingShare, NativeShareProfileContent } =
    useShareProfile(profile, isMyProfile)

  useClickOutside(trayShareButtonElRef, () =>
    isShareContextMenuOpen ? setIsShareContextMenuOpen(false) : undefined
  )

  // ===========================
  // Set Card Half Open on Mount
  // ===========================
  const scrollToDefaultProfileState = React.useCallback(
    (scrollBehavior?: ScrollBehavior) => {
      if (!containerElRef.current || !profileCardContainerElRef.current) return
      const top =
        profileCardContainerElRef.current.scrollHeight - LocalConstants.CardHalfOpenDisplayPoint
      containerElRef.current.scrollTo({ top, behavior: scrollBehavior ?? 'smooth' })
    },
    [containerElRef, profileCardContainerElRef]
  )

  React.useEffect(() => {
    scrollToDefaultProfileState('instant')
  }, [scrollToDefaultProfileState])

  // =========================
  // Scroll Container Listener
  // =========================
  React.useEffect(() => {
    if (
      !containerElRef.current ||
      !trayHeadElRef.current ||
      !profileCardElRef.current ||
      !profileCardContainerElRef.current
    )
      return

    const scrollEl = containerElRef.current
    const trayHeadEl = trayHeadElRef.current
    const cardContainerEl = profileCardContainerElRef.current
    const cardEl = profileCardElRef.current

    let prevShouldPinTrayValue: boolean

    const handleScroll = () => {
      const shouldPinTray =
        trayHeadEl.getBoundingClientRect().top <= scrollEl.getBoundingClientRect().top
      if (prevShouldPinTrayValue !== shouldPinTray) {
        trayHeadEl.style.borderRadius = shouldPinTray ? '0' : ''
        prevShouldPinTrayValue = shouldPinTray
        setIsTrayPinned(shouldPinTray)
      }

      // Subtle card parallax on scroll reveal
      const scrollTop = scrollEl.scrollTop
      const scrollMidpoint = cardContainerEl.scrollHeight - LocalConstants.CardHalfOpenDisplayPoint

      if (scrollTop < scrollMidpoint) {
        const scrollPercent = scrollTop / scrollMidpoint
        cardEl.style.transform = `translateY(calc(${spacing.big} * ${1 - scrollPercent}))`
      }

      // Hide tray share button when large profile share button is visible
      if (trayShareButtonElRef.current) {
        trayShareButtonElRef.current.style.opacity = scrollTop <= 40 ? '0' : '1'
      }
    }
    scrollEl.addEventListener('scroll', handleScroll)

    return () => scrollEl.removeEventListener('scroll', handleScroll)
  }, [containerElRef])

  // ==========================
  // Scroll Restore on Back Nav
  // ==========================

  const isUsingScrollRestore = useScrollRestoreOverride({
    hasPageLoaded: true,
    customScrolledElement: containerElRef,
    shouldOnlyScrollOnBackNav: true,
  })

  // ========================
  // Handlers & Derived State
  // ========================

  const handleScrollContentIntoView = () => {
    if (
      containerElRef.current &&
      trayProfileInfoElRef.current &&
      profileCardContainerElRef.current
    ) {
      const cardHeight = profileCardContainerElRef.current.offsetHeight
      const trayInfoHeight = trayProfileInfoElRef.current.offsetHeight
      containerElRef.current.scrollTo({
        top: trayInfoHeight + cardHeight,
        behavior: isTrayPinned ? 'instant' : 'smooth',
      })
    }
  }

  const handleBetsTabClick = () => {
    setCurrentContentDisplay('pending-bets', { shouldReplace: true })
    trackBetLoopProfileTabAndPillChange('Tab Change', 'Bets', 'Pending Shares')
    handleScrollContentIntoView()
  }

  const handleStatsTabClick = () => {
    setCurrentContentDisplay('stats', { shouldReplace: true })
    trackBetLoopProfileTabAndPillChange('Tab Change', 'Stats')
    handleScrollContentIntoView()
  }

  const createBetsOptionClickHandler =
    (option: Exclude<TabContentOptions, 'stats'>) => (e: React.MouseEvent) => {
      e.currentTarget.scrollIntoView({ behavior: 'smooth', inline: 'nearest' })
      setCurrentContentDisplay(option, { shouldReplace: true })
      trackBetLoopProfileTabAndPillChange('Pill Change', 'Bets', betTabPillsDict[option])
      handleScrollContentIntoView()
    }

  const handleIncomingShareTabChange = (tabContent: TabContentOptions) => {
    setCurrentContentDisplay(tabContent, {
      shouldReplace: true,
      paramsToDelete: [IncomingBetOpenPostIdSearchParam, IncomingBetViewPostIdSearchParam],
    })
  }

  const isProfileEmptyState = isMyProfile
    ? profile.shareCount === 0 && !hasCopiedBets
    : profile.shareCount === 0

  return (
    <WrapperStyled ref={containerElRef}>
      <div className='profile-page__card' ref={profileCardContainerElRef}>
        <ProfileCard profile={profile} ref={profileCardElRef} />

        {canShare && (
          <Button
            size='md'
            isFullWidth
            color='secondary_outline'
            disabled={isLoadingShare}
            isLoading={isLoadingShare}
            onClick={handleShareProfileClick}
          >
            Share Profile Card <Icon name='LineShare02' size='2rem' />
          </Button>
        )}
      </div>

      <div className='profile-page__tray'>
        <header ref={trayHeadElRef}>
          <h2>
            {profile.displayName}

            {profile.type !== 'Profile' && (
              <Icon name='SolidCheckVerified02' color={colors.studio[600]} size={spacing.md} />
            )}
          </h2>

          <div ref={trayShareButtonElRef}>
            {isMyProfile ? (
              canShare && (
                <PillButton
                  size='xs'
                  isIconOnlyButton
                  color='secondary_grey'
                  disabled={isLoadingShare}
                  isLoading={isLoadingShare}
                  aria-label='Share Profile'
                  data-testid='MyProfile.Share'
                  onClick={handleShareProfileClick}
                >
                  <Icon name='LineShare02' size='2rem' />
                </PillButton>
              )
            ) : (
              <>
                <PillButton
                  size='xs'
                  isIconOnlyButton
                  color='secondary_grey'
                  data-testid='shared-bet-context-menu-btn'
                  onClick={() => setIsShareContextMenuOpen(curr => !curr)}
                >
                  <Icon name='DuocolorDotsHorizontal' color={colors.studio[600]} size='2rem' />
                </PillButton>

                <ContextMenu
                  isOpen={isShareContextMenuOpen}
                  referenceElRef={trayShareButtonElRef}
                  offsetYPx={5}
                  placement='bottom-end'
                  onDismiss={() => setIsShareContextMenuOpen(false)}
                >
                  <Button
                    size='sm'
                    disabled={isLoadingShare}
                    isLoading={isLoadingShare}
                    color='secondary_outline'
                    onClick={handleShareProfileClick}
                  >
                    <div style={{ minWidth: '12rem' }}>
                      <Icon name='LineShare02' />
                      <span>Share Profile</span>
                    </div>
                  </Button>

                  <ReportProfileButton onClick={() => setIsReportProfileModalOpen(true)} />
                </ContextMenu>
              </>
            )}
          </div>

          <ReportProfileModal
            isOpen={isReportProfileModalOpen}
            onDismiss={() => setIsReportProfileModalOpen(false)}
            accountId={profile.id}
          />
        </header>

        <div className='profile-page__tray-profile-info' ref={trayProfileInfoElRef}>
          <FollowStats
            id={profile.id}
            isMyProfile={isMyProfile}
            followerCount={profile.followerCount}
            followingCount={profile.followingCount}
            shareCount={profile.shareCount}
          />

          {/* TEMPORARY: AML Compliance - Bio/description hidden */}
          {/* <ProfileBio description={profile.description} /> */}

          <PrimaryActionSection profile={profile} isMyProfile={isMyProfile} />

          {isProfileEmptyState && <div />}
        </div>

        {!isProfileEmptyState && (
          <div className='profile-page__tray-tabs'>
            <Tabs>
              <TabButton
                content='Bets'
                isSelected={currentContentDisplay !== 'stats'}
                onClick={handleBetsTabClick}
              />

              <TabButton
                content='Stats'
                isSelected={currentContentDisplay === 'stats'}
                onClick={handleStatsTabClick}
              />
            </Tabs>

            {currentContentDisplay !== 'stats' && (
              <div className='profile-page__bets-options'>
                {betOptions.map(option => (
                  <PillButton
                    size='xs'
                    key={option}
                    role='button'
                    onClick={createBetsOptionClickHandler(option)}
                    color={currentContentDisplay === option ? 'primary' : 'secondary_color'}
                  >
                    {betTabPillsDict[option]}

                    {option === 'pending-bets' &&
                    pendingBetsCount !== null &&
                    pendingBetsCount > 0 ? (
                      <span>{pendingBetsCount}</span>
                    ) : null}
                  </PillButton>
                ))}
              </div>
            )}
          </div>
        )}

        <div className='profile-page__tray-content'>
          {isProfileEmptyState ? (
            <EmptyProfile isMyProfile={isMyProfile} />
          ) : (
            <>
              {currentContentDisplay !== 'stats' && (
                <FeedContainerStyled>
                  <ProfileContext.Provider
                    value={{
                      isOnProfilePage: true,
                      profilePageId: profile.id,
                      scrollToDefaultProfileState,
                    }}
                  >
                    <ProfileBets
                      profile={profile}
                      setPendingCount={setPendingBetsCount}
                      setHasCopiedBets={setHasCopiedBets}
                      currentContentDisplay={currentContentDisplay}
                      handleIncomingShareTabChange={handleIncomingShareTabChange}
                      isUsingScrollRestore={isUsingScrollRestore}
                    />
                  </ProfileContext.Provider>
                </FeedContainerStyled>
              )}

              {currentContentDisplay === 'stats' && <ProfileStats id={profile.id} />}
            </>
          )}

          <NativeShareProfileContent />
        </div>
      </div>

      {currentContentDisplay !== 'stats' && isMyProfile && (
        <QuickShareButton isOnProfile shouldHideButton={!isTrayPinned} />
      )}
    </WrapperStyled>
  )
}

// =============
// Local Helpers
// =============

const betTabPillsDict: Record<Exclude<TabContentOptions, 'stats'>, BetOptions> = {
  'my-copies': 'My Copies',
  'pending-bets': 'Pending Shares',
  'resulted-bets': 'Resulted Shares',
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  '--dynamic-height': '100dvh',

  position: 'fixed',
  top: measurements.mobi.headerHeight,
  bottom: 0,
  width: '100%',
  maxWidth: measurements.mobi.maxWidth,
  minWidth: measurements.mobi.minWidth,
  overflowY: 'auto',
  height: `calc(var(--dynamic-height, 100vh) - ${measurements.mobi.headerHeight} - ${measurements.mobi.bottomTabsHeight})`,

  overflowX: 'auto',
  scrollBehavior: 'smooth',
  WebkitOverflowScrolling: 'touch',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
  overscrollBehavior: 'contain',

  '> .profile-page__card': {
    boxSizing: 'border-box',
    position: 'sticky',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: spacing.bigx3,
    maxWidth: '35rem',
    padding: `${spacing.bigx3} ${spacing.md}`,
    margin: '0 auto',

    animation: 'ease animateCardIn 0.5s forwards',
    '@keyframes animateCardIn': {
      from: { transform: 'translateY(50px)', opacity: 0.6 },
      to: { transform: 'translateY(0px)', opacity: 1 },
    },

    // Card Display
    '> div:first-of-type': {
      transition: 'transform 0.1s ease',
      boxShadow: `0 0 30px -5px ${colors.studio[500]}, 0 0.309px 1.234px 0 rgba(0, 0, 0, 0.25)`,
    },

    '> button': { marginTop: spacing.big },
  },

  '> .profile-page__tray': {
    position: 'relative',
    zIndex: 2,
    display: 'flex',
    flexDirection: 'column',
    minHeight: `calc(var(--dynamic-height, 100vh) - ${measurements.mobi.headerHeight} - ${measurements.mobi.bottomTabsHeight})`,
    transform: 'translate3d(0,0,0)',

    border: `1px solid ${colors.neutral[200]}`,
    borderBottom: 0,
    borderTopLeftRadius: radius.lgx6,
    borderTopRightRadius: radius.lgx6,
    backgroundColor: colors.white,
    boxShadow:
      '0 -54px 15px 0 rgba(0, 0, 0, 0.00), 0 -35px 14px 0 rgba(0, 0, 0, 0.01), 0 -20px 12px 0 rgba(0, 0, 0, 0.03), 0 -9px 9px 0 rgba(0, 0, 0, 0.04), 0 -2px 5px 0 rgba(0, 0, 0, 0.05)',

    '> header': {
      boxSizing: 'border-box',
      position: 'sticky',
      top: 0,
      zIndex: 5,
      display: 'flex',
      alignItems: 'center',
      padding: `${spacing.md} calc(3.2rem + ${spacing.md}) 0`,
      height: `calc(3.2rem + ${spacing.md})`,
      borderTopLeftRadius: radius.lgx6,
      borderTopRightRadius: radius.lgx6,
      backgroundColor: colors.white,
      transition: 'border-radius 0.2s ease, box-shadow 0.3s ease',

      '::after': {
        content: '""',
        position: 'absolute',
        top: spacing.smx1,
        left: '50%',
        width: '4.4rem',
        height: '0.6rem',
        borderRadius: '2.1rem',
        background: colors.surface[400],
        transform: 'translateX(-50%)',

        '@media (hover: hover) and (pointer: fine)': { display: 'none' },
      },

      '::before': {
        content: '""',
        position: 'absolute',
        top: '100%',
        right: 0,
        left: 0,
        height: spacing.smx1,
        background: `linear-gradient(to bottom, ${colors.white}, transparent)`,
      },

      '> h2': {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: spacing.smx2,
        padding: 0,
        margin: 0,
        background: 0,
        color: colors.black,
        fontFamily: font.family.primary,
        fontSize: font.size.xl2.fontSize,
        letterSpacing: font.size.xl2.letterSpacing,
        lineHeight: font.size.xl2.lineHeight,
        fontWeight: font.weight.semibold,
        textShadow: 'none',
        textTransform: 'unset',
      },

      // Share / Context Button
      '> div': {
        position: 'absolute',
        top: spacing.md,
        right: spacing.md,
        transition: 'opacity 0.15s ease',
      },
    },

    '> .profile-page__tray-profile-info': {
      position: 'relative',
      zIndex: 4,
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      gap: spacing.md,
      padding: `${spacing.smx1} ${spacing.md} 0`,
    },

    '> .profile-page__tray-tabs': {
      position: 'sticky',
      top: `calc(3.2rem + ${spacing.md})`,
      zIndex: 3,
      background: colors.white,

      '.profile-page__bets-options': {
        boxSizing: 'border-box',
        display: 'flex',
        gap: spacing.smx1,
        alignItems: 'center',
        padding: `0 ${spacing.md}`,
        minHeight: '4.8rem',
        background: colors.white,
        borderBottom: `0.1rem solid ${colors.neutral[200]}`,
        overflowX: 'auto',
        scrollbarWidth: 'thin',

        '@media (pointer: coarse)': {
          scrollbarWidth: 'none',
          '::-webkit-scrollbar': { display: 'none' },
        },

        '> button': {
          flexShrink: 0,

          '> span': {
            borderRadius: spacing.md,
            backgroundColor: colors.lavender[100],
            padding: `0.2rem ${spacing.smx1}`,
            color: colors.lavender[600],
            display: 'flex',
            height: spacing.md,
            justifyContent: 'center',
            alignItems: 'center',
          },
        },
      },
    },

    '> .profile-page__tray-content': {
      flex: 1,
      background: colors.surface[300],
      paddingBottom: spacing.bigx1,
      minHeight: `calc(var(--dynamic-height, 100vh) - ${measurements.mobi.headerHeight} - ${measurements.mobi.bottomTabsHeight})`,
    },
  },
})
